import { useCallback, useEffect } from 'react';
import { useLingui } from '@lingui/react';
import { formatMoneyFixed } from '@wkda/funnel-utils';
import { CheveronRightIcon } from '@wkda/icons';
import { Button } from '@wkda/styleguide';
import { useCarleadConfiguration } from '../../hooks/useCarleadCreationConfiguration';
import { useCarleadDataLayer } from '../../hooks/useCarleadDataLayer';
import { CustomerComment } from './CustomerComment';
import { useTrackCustomerBanner } from '@wkda/funnel-components';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getDaysToExpireText(i18n, priceExpiresInDays) {
  if (typeof priceExpiresInDays !== 'number') return '';
  return priceExpiresInDays === 1 ? i18n._('carleadCreation.onsiteValidRCB.titleStillValidToday') : priceExpiresInDays === 2 ? i18n._('carleadCreation.onsiteValidRCB.titleOneDayOnly') : priceExpiresInDays > 2 ? i18n._('carleadCreation.onsiteValidRCB.titleDaysRemaining', {
    priceExpiresInDays: priceExpiresInDays - 1
  }) : '';
}
export function BannerContentOnsiteValid(_ref) {
  let {
    isRiskFree,
    priceExpiresInDays,
    price,
    manufacturer,
    bookingUrl,
    auctionSummaryUrl
  } = _ref;
  const {
    i18n: _i18n
  } = useLingui();
  const dataLayer = useCarleadDataLayer();
  const {
    locale
  } = useCarleadConfiguration();
  const title = price ? _i18n._('carleadCreation.onsiteValidRCB.titlePrice', {
    manufacturer
  }) : _i18n._('carleadCreation.onsiteValidRCB.titleNoPrice', {
    manufacturer
  });
  const formattedPriceWithCurrency = formatMoneyFixed(locale, price ?? 0);
  const daysToExpireText = getDaysToExpireText(_i18n, priceExpiresInDays);
  const {
    trackShownEvent,
    trackCTAClickEvent,
    getCJTrackingButtonNameByAttr
  } = useTrackCustomerBanner();
  useEffect(() => {
    dataLayer.pushCustomerBannerOpen('on-site-price');
    trackShownEvent('on-site-price-valid');
  }, [dataLayer, trackShownEvent]);
  const handleButtonClick = useCallback(e => {
    const ctaName = getCJTrackingButtonNameByAttr(e.currentTarget);
    if (ctaName) {
      trackCTAClickEvent('on-site-price-valid', ctaName);
    }
  }, [getCJTrackingButtonNameByAttr, trackCTAClickEvent]);
  return /*#__PURE__*/_jsx("div", {
    className: "container onsite-valid-rcb",
    children: /*#__PURE__*/_jsxs("div", {
      className: "row text-center",
      children: [!isRiskFree && /*#__PURE__*/_jsx("div", {
        className: "d-none d-md-flex col-sm-3",
        children: /*#__PURE__*/_jsx("div", {
          className: "onsite-valid-rcb__ribbon",
          children: daysToExpireText
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: "col-sm-12 col-md-6",
        children: [/*#__PURE__*/_jsx("div", {
          className: "onsite-valid-rcb__title",
          children: title
        }), !!price && !isRiskFree && /*#__PURE__*/_jsxs("div", {
          className: "onsite-valid-rcb__price-wrapper",
          children: [/*#__PURE__*/_jsx("div", {
            className: "banner-return-customer__price",
            children: formattedPriceWithCurrency
          }), /*#__PURE__*/_jsx("div", {
            className: `onsite-valid-rcb__ribbon-small d-md-none`,
            children: daysToExpireText
          })]
        }), isRiskFree ? /*#__PURE__*/_jsx(Button, {
          "data-cj-tracking-button-name": "auction-summary-button",
          component: "a",
          variant: "secondary",
          size: "lg",
          href: auctionSummaryUrl,
          onClick: handleButtonClick,
          children: /*#__PURE__*/_jsxs("div", {
            className: "d-flex justify-content-center align-items-center",
            children: [/*#__PURE__*/_jsx("span", {
              className: "d-none d-md-block",
              children: _i18n._('carleadCreation.onsiteValidRCB.ctaRiskFreeFull')
            }), /*#__PURE__*/_jsx("span", {
              className: "d-md-none",
              children: _i18n._('carleadCreation.onsiteValidRCB.ctaRiskFreeShort')
            }), /*#__PURE__*/_jsx(CheveronRightIcon, {
              className: "ml-2"
            })]
          })
        }) : /*#__PURE__*/_jsx(Button, {
          "data-cj-tracking-button-name": "view-booking-button",
          component: "a",
          variant: "secondary",
          size: "lg",
          href: bookingUrl,
          onClick: handleButtonClick,
          children: /*#__PURE__*/_jsxs("div", {
            className: "d-flex justify-content-center align-items-center",
            children: [/*#__PURE__*/_jsx("span", {
              className: "d-none d-md-block",
              children: _i18n._('carleadCreation.onsiteValidRCB.ctaBookingFull')
            }), /*#__PURE__*/_jsx("span", {
              className: "d-md-none",
              children: _i18n._('carleadCreation.onsiteValidRCB.ctaBookingShort')
            }), /*#__PURE__*/_jsx(CheveronRightIcon, {
              className: "ml-2"
            })]
          })
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "d-none d-md-flex col-md-3 justify-content-center align-items-center",
        children: /*#__PURE__*/_jsx(CustomerComment, {
          title: _i18n._('carleadCreation.onsiteValidRCB.customersTitle')
        })
      })]
    })
  });
}